// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsTransformationGetAllAttrs,
  IAboutUsTransformationGetAttrs,
  IAboutUsTransformationSaveAttrs,
} from 'interfaces/back-office/content-management/about-us/transformation/about-us-transformation-view.interface'
import {
  IAboutUsTransformationApiGetAllResponse,
  IAboutUsTransformationApiGetResponse,
  IAboutUsTransformationApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/transformation/about-us-transformation-api.interface'
import { IAboutUsTransformation } from 'interfaces/back-office/content-management/about-us/transformation'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// About Us Transformation Props
const aboutUsTransformationProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

const AboutUsTransformations = types.array(
  types.model('AboutUsTransformations').props({
    ...aboutUsTransformationProps,
    aboutUsTransformations: types.maybeNull(types.array(types.model('AboutUsTransformations').props(aboutUsTransformationProps))),
  }),
)

const AboutUsTransformation = types.model('aboutUsTransformation').props(aboutUsTransformationProps)

const AboutUsTransformationStoreModel = types
  .model('aboutUsTransformation')
  .props({
    aboutUsTransformation_isLoading: types.optional(types.boolean, false),
    aboutUsTransformation_isSaveLoading: types.optional(types.boolean, false),
    aboutUsTransformation_aboutUsTransformations: types.optional(AboutUsTransformations, []),
    aboutUsTransformation_aboutUsTransformation: types.optional(AboutUsTransformation, {}),
  })
  .actions(self => ({
    aboutUsTransformation_SET_LOADING: (payload: boolean): void => {
      self.aboutUsTransformation_isLoading = payload
    },
    aboutUsTransformation_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsTransformation_isSaveLoading = payload
    },
    aboutUsTransformation_SET_ABOUT_US_TRANSFORMATIONS: (payload: IAboutUsTransformation[]): void => {
      self.aboutUsTransformation_aboutUsTransformations = cast(
        payload?.map((list, index) => ({ ...list, number: index + 1 })) || [],
      )
    },
    aboutUsTransformation_SET_ABOUT_US_TRANSFORMATION: (payload: IAboutUsTransformation): void => {
      self.aboutUsTransformation_aboutUsTransformation = cast(payload)
    },
    aboutUsTransformation_CLEAR_ABOUT_US_TRANSFORMATIONS: (): void => {
      self.aboutUsTransformation_aboutUsTransformations = cast([])
    },
    aboutUsTransformation_CLEAR_ABOUT_US_TRANSFORMATION: (): void => {
      self.aboutUsTransformation_aboutUsTransformation = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about us transformations
     *
     * @param {IAboutUsTransformationGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsTransformation_fetchAboutUsTransformations: flow(function* (payload: IAboutUsTransformationGetAllAttrs) {
      self.aboutUsTransformation_SET_LOADING(true)

      const response: IAboutUsTransformationApiGetAllResponse =
        yield self.environment.aboutUsTransformationApi.aboutUsTransformation_getAboutUsTransformations({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.aboutUsTransformation_SET_ABOUT_US_TRANSFORMATIONS(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsTransformation_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us transformations
     *
     * @param {IAboutUsTransformationGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsTransformation_fetchAboutUsTransformation: flow(function* (payload: IAboutUsTransformationGetAttrs) {
      self.aboutUsTransformation_SET_LOADING(true)

      const response: IAboutUsTransformationApiGetResponse =
        yield self.environment.aboutUsTransformationApi.aboutUsTransformation_getAboutUsTransformation({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.aboutUsTransformation_SET_ABOUT_US_TRANSFORMATION(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsTransformation_SET_LOADING(false)
    }),

    /**
     * @description Save about us transformation
     *
     * @param {IAboutUsTransformationSaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsTransformation_saveAboutUsTransformation: flow(function* (payload: IAboutUsTransformationSaveAttrs) {
      self.aboutUsTransformation_SET_SAVE_LOADING(true)

      const response: IAboutUsTransformationApiSaveResponse =
        yield self.environment.aboutUsTransformationApi.aboutUsTransformation_saveAboutUsTransformation({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsTransformation_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsTransformationStoreDefaultModel = () => types.optional(AboutUsTransformationStoreModel, {})
